import { Injectable } from "@angular/core";
import { ClientTariff } from "../../shared/constants";
import { House } from "../../entities/houses/houses.model";
import { HOUSE_TARIFF } from "../../entities/houses/houses.constants";
import { CognitoUser } from "amazon-cognito-identity-js";
import { CookieService } from "ngx-cookie-service";

@Injectable()
export class SessionStateService {
  constructor(private readonly _cookiesService: CookieService) {}

  public getDevicesOrder(): Map<string, string[]> {
    const key = `devicesOrder#${this.getHouse().houseId}#${this.getUsername()}`;
    const devicesOrderMap: Map<string, string[]> = this.getMap(key);
    if (devicesOrderMap === undefined) {
      this.setDevicesOrder(new Map<string, string[]>());
    }
    return this.getMap(key);
  }

  public setDevicesOrder(devicesOrder: Map<string, string[]>) {
    this.storeMap(
      `devicesOrder#${this.getHouse().houseId}#${this.getUsername()}`,
      devicesOrder
    );
  }

  public setUserId(userid: string) {
    this.store("userid", userid);
  }

  public getUserId() {
    return this.get("userid");
  }

  public setUsername(username: string) {
    this.store("username", username);
  }

  public getReportsUsername() {
    return this.get("reports_username");
  }

  public getReportsToken(): string {
    return this.get("reportsToken");
  }

  public getUsername(): string {
    return this.get("username");
  }

  public setReportsToken(reportsToken: string, smartDeviceId: string): void {
    this.store("reportsToken", reportsToken);
    this.store("reports_username", smartDeviceId);
  }

  public getSmartDevices(): any {
    const smartDevicesJson = this.get("smartDevices");

    return JSON.parse(smartDevicesJson || "[]");
  }

  public setSmartDevices(smartDevices: any): void {
    this.store("smartDevices", JSON.stringify(smartDevices));
  }

  public getHouseTariff(): any {
    return this.get("houseTariff");
  }

  public setHouseTariff(houseTariff: any): void {
    this.store("houseTariff", houseTariff);
  }

  public getSmartDeviceConnectionStatus(): boolean {
    return this.get("smartDeviceConnection");
  }

  public setSmartDevicesConnectionStatus(connection: boolean) {
    this.store("smartDeviceConnection", connection);
  }

  public clearSession(): void {
    this._cookiesService.deleteAll();
  }

  private clearCookies(): void {
    const allCookies = document.cookie.split(";");
    const cookiesKeys = allCookies.map((cookie) => cookie.split("=")[0]);
    cookiesKeys.forEach((cookie) => {
      document.cookie = `${cookie}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    });
  }

  public getJwtIdToken(): string {
    return this._cookiesService.get("jwtIdToken") !== null
      ? this._cookiesService.get("jwtIdToken")
      : undefined;
  }

  public store(key: string, value: any): void {
    this._cookiesService.set(key, JSON.stringify(value), 90);
  }

  public get(key: string) {
    const cookie = this._cookiesService.get(key);

    return cookie !== "" ? JSON.parse(cookie) : 0;
  }

  public getMap(key: string): any {
    const cookie = this._cookiesService.get(key);

    return cookie ? new Map(JSON.parse(cookie)) : undefined;
  }

  public storeMap(key: string, storeMap: Map<string, any>): void {
    const entries = JSON.stringify(Array.from(storeMap.entries()));

    this._cookiesService.set(key, entries, 90);
  }

  public getCookiesAcceptance(): boolean {
    return this.get("cookiesAcceptance");
  }

  public setCookiesAcceptance(cookies: boolean) {
    this.store("cookiesAcceptance", cookies);
  }

  public getLang(): string {
    return this.get("language");
  }

  public setLang(lang: string) {
    this.store("language", lang);
  }

  public getHouse(): House {
    return this.get(`house#${this.getUsername()}`);
  }

  public setHouse(house: House) {
    this.store(`house#${this.getUsername()}`, house);
  }

  public setFirstLogin(login: string) {
    this.store("firstLogin", login);
  }

  public getFirstLogin(): string {
    return this.get("firstLogin");
  }

  public deleteFirstLogin(): void {
    this._cookiesService.delete("firstLogin");
  }

  public setRememberMe(remember: boolean) {
    this.store("rememberMe", remember);
  }

  public getRememberMe(): boolean {
    return this.get("rememberMe");
  }

  public setCoachMarkState(state, firstLoginUsername?: string) {
    let username = firstLoginUsername ? firstLoginUsername : this.getUsername();
    return this.store(`coachmark#${username}`, state);
  }

  public getCoachMarkState(): boolean {
    return this.get(`coachmark#${this.getUsername()}`);
  }

  public getCoachMarkIndividualState(): Map<string, boolean> {
    let username = this.getUsername();
    const coachMarkIndividualStateMap: Map<string, boolean> = this.getMap(
      `CoachMarkIndividualState#${username}`
    );
    if (coachMarkIndividualStateMap === undefined) {
      this.initializeCoachMarkIndividualState(false);
    }
    return this.getMap(`CoachMarkIndividualState#${username}`);
  }

  public setCoachMarkIndividualState(
    coachMarkIndividualState: Map<string, boolean>,
    firstLoginUsername?: string
  ) {
    let username = firstLoginUsername ? firstLoginUsername : this.getUsername();
    this.storeMap(
      `CoachMarkIndividualState#${username}`,
      coachMarkIndividualState
    );
  }

  public initializeCoachMarkIndividualState(
    value: boolean,
    firstLoginUsername?: string
  ) {
    const coachMarkIndividualStateMap = new Map<string, boolean>();
    coachMarkIndividualStateMap.set("device-detail", value);
    coachMarkIndividualStateMap.set("device", value);
    coachMarkIndividualStateMap.set("tips", value);
    coachMarkIndividualStateMap.set("efficiency-tips", value);
    coachMarkIndividualStateMap.set("home", value);
    coachMarkIndividualStateMap.set("house-detail", value);
    coachMarkIndividualStateMap.set("house", value);
    coachMarkIndividualStateMap.set("users", value);
    coachMarkIndividualStateMap.set("scheduling", value);
    this.setCoachMarkIndividualState(
      coachMarkIndividualStateMap,
      firstLoginUsername
    );
    return coachMarkIndividualStateMap;
  }
}



// WEBPACK FOOTER //
// ./src/app/core/services/session-state.service.ts